import { effect, Injectable } from '@angular/core';
import { ServiceUnavailableInfoComponent } from '@login/service-unavailable-info/service-unavailable-info.component';
import { AlertHelper } from '@shared/alert/alert.helper';
import { Log } from '@shared/debug/log';
import { InitManager } from '@shared/manager/init.manager';
import { OverlayService } from '@shared/overlay/overlay.service';
import { WagoApiStatusCode } from '@shared/service/wago-api-status-code';
import { WagoApiClient } from '@shared/service/wago-api.client';
import { ErrorUtils } from '@shared/util/error-utils';
import { ErrorInfoVO } from '@shared/vo/error-info.vo';
import { WindowService } from '@shared/window/window.service';

@Injectable({providedIn: 'root'})
export class WagoApiNotifierService {

  constructor(
    private wagoApiClient: WagoApiClient,
    private alertHelper: AlertHelper,
    private windowService: WindowService,
    private overlayService: OverlayService,
    initManager: InitManager,
  ) {
    effect(() => {
      if(initManager.isCdcAvailable() === false) {
        this.windowService.showModalFullscreen(ServiceUnavailableInfoComponent);
      }
    });
  }

  public init(): void {
      this.wagoApiClient.$httpErrors.subscribe((error) => this.showErrorAlert(error));

      this.wagoApiClient.$initialRequestFailed.subscribe((failed) => {
        if(failed) this.windowService.showModalFullscreen(ServiceUnavailableInfoComponent);
      });

      this.wagoApiClient.$requestProgress.subscribe((progress) => {
        if (progress.waiting) {
          this.overlayService.requestOverlayShow(progress.overlayType);
        } else {
          this.overlayService.requestOverlayHide(progress.overlayType);
        }
      })
  }

  private async showErrorAlert(error: any): Promise<void> {
    const statusCode: number = WagoApiClient.getErrorStatusCode(error);
    const errorInfo: ErrorInfoVO = ErrorUtils.getErrorInfo(error);
    const errorMessageParam: string = Log.isDevMode ? error.statusText + '\n' + error.url : error.message ?? '';

    switch (statusCode) {
      case WagoApiStatusCode.RequestTimeout:
      case WagoApiStatusCode.BadRequest:
      case WagoApiStatusCode.GatewayTimeout:
      case WagoApiStatusCode.NetworkReadTimeoutError:
      case WagoApiStatusCode.CloudflareWebServerDown:
      case WagoApiStatusCode.CloudflareConnectionTimedOut:
      case WagoApiStatusCode.CloudflareOriginIsUnreachable:
      case WagoApiStatusCode.CloudflareTimeout:
        this.openErrorPopup('serverCommunication.message.no_connection', 'serverCommunication.title.no_connection', {0: errorMessageParam});
        break;
      case WagoApiStatusCode.ServiceUnavailable:
        this.openErrorPopup('serverCommunication.message.service_not_active', 'serverCommunication.title.service_not_active');
        break;
      case WagoApiStatusCode.Forbidden:
      case WagoApiStatusCode.MethodNotAllowed:
        this.openErrorPopup('serverCommunication.message.user_has_no_rights', 'serverCommunication.title.user_has_no_rights');
        break;
      case WagoApiStatusCode.NotFound:
        this.openErrorPopup('serverCommunication.message.not_found', 'serverCommunication.title.not_found');
        break;
      default:
        const errorTitle: string = Log.isDevMode && errorInfo.title ? errorInfo.title : 'serverCommunication.title.unexpected_problem';
        this.openErrorPopup('serverCommunication.message.unexpected_problem', errorTitle, {0: errorMessageParam});
    }
    error.faultMessageAlreadyDisplayed = true;
  }

  private async openErrorPopup(errorMessage: string, errorTitle: string, errorParams: any = {}): Promise<void> {
    await this.alertHelper.showError(errorTitle, errorMessage, {translationParams: errorParams});
  }

}
